import React, { useState } from 'react'
import { Alert, Button, Card, Input, Modal, Space, Typography, message } from 'antd'

import adminAxios from './adminAxios'
import { resolveFrontendApiBase } from '../utils/apiBase'
import './AdminMaintenanceTools.css'

const { Text } = Typography

const API_BASE = resolveFrontendApiBase()

type FrenchDialogueLegacyResetResponse = {
  bookId: string
  chapterGuid?: string | null
  pair?: string | null
  dryRun: boolean
  supportCode?: string | null
  translatedChaptersMatchedCount?: number
  translatedChaptersClearedCount?: number
  manifestsFailedCount?: number
  locksInvalidatedCount?: number
  runsFailedCount?: number
  candidateCounts?: {
    translatedChaptersToClear?: number
    manifestsToFail?: number
    locksToInvalidate?: number
    runsToFail?: number
  }
  samples?: {
    chapterGuids?: string[]
    manifestIds?: string[]
    lockGuids?: string[]
    runIds?: string[]
  }
  warnings?: string[]
}

const renderCount = (value?: number | null) => (typeof value === 'number' ? value.toLocaleString() : '0')

const AdminFrenchDialogueLegacyTab: React.FC = () => {
  const [bookId, setBookId] = useState('')
  const [chapterGuid, setChapterGuid] = useState('')
  const [reason, setReason] = useState('')
  const [preview, setPreview] = useState<FrenchDialogueLegacyResetResponse | null>(null)
  const [result, setResult] = useState<FrenchDialogueLegacyResetResponse | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [executeLoading, setExecuteLoading] = useState(false)

  const runReset = async (dryRun: boolean) => {
    const normalizedBookId = bookId.trim()
    if (!normalizedBookId) {
      message.warning('Enter a Book ID first.')
      return
    }

    const payload = {
      book_id: normalizedBookId,
      chapter_guid: chapterGuid.trim() || undefined,
      reason: reason.trim() || undefined,
      dry_run: dryRun,
    }

    if (dryRun) {
      setPreviewLoading(true)
    } else {
      setExecuteLoading(true)
    }

    try {
      const response = await adminAxios.post<FrenchDialogueLegacyResetResponse>(
        `${API_BASE}/admin/french-dialogue-legacy/reset-free-run`,
        payload
      )
      if (dryRun) {
        setPreview(response.data)
        setResult(null)
        message.success('Dry run completed.')
      } else {
        setPreview(response.data)
        setResult(response.data)
        message.success('Free cleanup reset completed.')
      }
    } catch (error: any) {
      const detail =
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to reset French dialogue cleanup state'
      message.error(typeof detail === 'string' ? detail : JSON.stringify(detail))
    } finally {
      if (dryRun) {
        setPreviewLoading(false)
      } else {
        setExecuteLoading(false)
      }
    }
  }

  const confirmAndExecuteReset = () => {
    Modal.confirm({
      title: 'Execute French dialogue cleanup reset?',
      content:
        'Run a preview first and confirm the affected chapters, manifests, locks, and runs before applying this reset.',
      okText: 'Execute reset',
      okButtonProps: { danger: true },
      onOk: () => runReset(false),
    })
  }

  const payload = result || preview

  return (
    <Card title="French Dialogue Free Cleanup Reset" className="admin-maintenance-tool">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Text type="secondary" className="admin-maintenance-tool__purpose">
          Preview and clear stuck free French dialogue cleanup state for a book or chapter while preserving an audit reason.
        </Text>
        <Alert
          className="admin-maintenance-tool__effect"
          type="warning"
          showIcon
          message="This admin tool clears the free French dialogue cleanup state for a whole book or a single chapter, and invalidates active cleanup manifests and locks in the selected scope."
        />
        <div className="admin-maintenance-tool__section admin-maintenance-tool__field-stack">
          <Input
            value={bookId}
            onChange={(event) => setBookId(event.target.value)}
            placeholder="Book ID"
          />
          <Input
            value={chapterGuid}
            onChange={(event) => setChapterGuid(event.target.value)}
            placeholder="Chapter GUID (optional)"
          />
          <Input.TextArea
            rows={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Reason for audit/support logs"
          />
        </div>
        <Space className="admin-maintenance-tool__controls admin-maintenance-tool__actions" wrap>
          <Button onClick={() => runReset(true)} loading={previewLoading}>
            Preview reset
          </Button>
          <Button
            danger
            onClick={confirmAndExecuteReset}
            loading={executeLoading}
            disabled={previewLoading}
          >
            Execute reset
          </Button>
        </Space>
        <Card
          className="admin-maintenance-tool__result"
          type="inner"
          size="small"
          title={result && !result.dryRun ? 'Reset Result' : 'Reset Preview'}
        >
          {payload ? (
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Text>
                Support code: <Text code>{payload.supportCode || '—'}</Text>
              </Text>
              <Text>
                Scope: <Text code>{payload.bookId}</Text>
                {payload.chapterGuid ? <> / <Text code>{payload.chapterGuid}</Text></> : ' / whole book'}
              </Text>
              <Text>Pair: {payload.pair || '—'}</Text>
              <Text>
                Matching translated chapters: {renderCount(payload.translatedChaptersMatchedCount)}
              </Text>
              <Text>
                Chapters to clear:{' '}
                {renderCount(payload.candidateCounts?.translatedChaptersToClear)}
              </Text>
              <Text>Cleanup manifests to fail: {renderCount(payload.candidateCounts?.manifestsToFail)}</Text>
              <Text>
                Cleanup locks to invalidate: {renderCount(payload.candidateCounts?.locksToInvalidate)}
              </Text>
              <Text>Cleanup runs to fail: {renderCount(payload.candidateCounts?.runsToFail)}</Text>
              {!payload.dryRun && (
                <>
                  <Text>
                    Chapters cleared: {renderCount(payload.translatedChaptersClearedCount)}
                  </Text>
                  <Text>Manifests failed: {renderCount(payload.manifestsFailedCount)}</Text>
                  <Text>Locks invalidated: {renderCount(payload.locksInvalidatedCount)}</Text>
                  <Text>Runs failed: {renderCount(payload.runsFailedCount)}</Text>
                </>
              )}
              {(payload.samples?.chapterGuids || []).length > 0 && (
                <Text>Sample chapters: {(payload.samples?.chapterGuids || []).join(', ')}</Text>
              )}
              {(payload.warnings || []).length > 0 && (
                <Alert
                  type="warning"
                  showIcon
                  message={(payload.warnings || []).slice(0, 3).join(' | ')}
                />
              )}
            </Space>
          ) : (
            <Text type="secondary">
              Leave Chapter GUID blank to reset the whole book cleanup pass. Run a preview first to inspect impact.
            </Text>
          )}
        </Card>
      </Space>
    </Card>
  )
}

export default AdminFrenchDialogueLegacyTab
